import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { APPROVED, YES, WORKFLOW_RERUN, VIEW } from "const"
import { CcRequirement, CcRequirementHistory } from "server/models"

import { hasProjectAccess } from "client/utils"
import {
    PRM_CC_DRAFTS,
    StoreType,
    DisplayColumns,

    showCcRequirementColumnSettings,
    setCcRequirementSelectedRequirementIds,
    setCcRequirementPopupMenuTarget,
    setCcRequirementPopupMenuShow,
    setCcRequirementActive,
} from "client/store"
import {
    UserIcon,
    TableColumn,
    NotAssigned,
    CheckButton,
    SettingsButton,
    TableMenu,
    DraftSaved,
    Empty,
} from "client/components"

import RequirementRevisionList from "./revisionList"
import InputSelectAll from "./selectAll"
import Open from "../open"

const Columns = (editAccess: boolean) => {
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseReviewRoute)
    const revisionActiveList = useSelector((state: StoreType) => state.ccRequirement.revisionActiveList)
    const requirementStateList = useSelector((state: StoreType) => state.ccRequirement.requirementStateList)
    const requirementHistoryList = useSelector((state: StoreType) => state.ccRequirement.requirementHistoryList)
    const tempAnswerList = useSelector((state: StoreType) => state.ccRequirement.tempAnswerList)
    const selectedRequirementIds = useSelector((state: StoreType) => state.ccRequirement.selectedRequirementIds)

    const typeList = useSelector((state: StoreType) => state.ccRequirementType.typeList)
    const priorityList = useSelector((state: StoreType) => state.ccRequirementPriority.priorityList)
    const statusList = useSelector((state: StoreType) => state.ccRequirementStatus.statusList)

    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)
    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[3] || []

    const reviewColumns = useSelector((state: StoreType) => state.system.userSettings.displayColumns.requirementReview)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()


    const displayColumns: DisplayColumns = {
        uniqueId: reviewColumns.uniqueId !== undefined ? reviewColumns.uniqueId : true,
        name: reviewColumns.name !== undefined ? reviewColumns.name : true,
        revision: reviewColumns.revision !== undefined ? reviewColumns.revision : true,
        type: reviewColumns.type !== undefined ? reviewColumns.type : true,
        priority: reviewColumns.priority !== undefined ? reviewColumns.priority : true,
        status: reviewColumns.status !== undefined ? reviewColumns.status : true,
        state: reviewColumns.state !== undefined ? reviewColumns.state : true,
    }
    levelIds.forEach((levelId) => {
        const columnId = `level_${levelId}`
        displayColumns[columnId] = reviewColumns[columnId] !== undefined ? reviewColumns[columnId] : true
        const columnAnswerId = `${columnId}_answer`
        displayColumns[columnAnswerId] = reviewColumns[columnAnswerId] !== undefined ? reviewColumns[columnAnswerId] : true
    })

    const viewDraftsAccess = hasProjectAccess(PRM_CC_DRAFTS, VIEW)

    const selectRequirement = (requirementId: number) => {
        const checked = selectedRequirementIds.includes(requirementId)
        let newSelected: number[] = selectedRequirementIds.slice()
        if (checked) {
            newSelected = newSelected.filter((id) => id !== requirementId)
        } else {
            newSelected.push(requirementId)
        }
        dispatch(setCcRequirementSelectedRequirementIds(newSelected))
    }

    const showColumnSettings = () => {
        dispatch(showCcRequirementColumnSettings(true))
    }

    const showContextMenu = (
        e: React.MouseEvent,
        active: CcRequirement,
    ) => {
        e.preventDefault()
        e.stopPropagation()

        dispatch(setCcRequirementPopupMenuTarget(e.currentTarget as HTMLElement))
        dispatch(setCcRequirementPopupMenuShow(true))
        dispatch(setCcRequirementActive(active))
    }

    const onClick = (requirement: CcRequirement) => {
        const activeRevision = revisionActiveList[requirement.id]
        if (!activeRevision) return
        Open(baseRoute, requirement.id, activeRevision.id, dispatch)
    }

    const columns: TableColumn[] = [
        {
            field: "setting",
            headerFormatter: () => <SettingsButton onClick={showColumnSettings} />,
            cellMinWidth: "46px",
            cellMaxWidth: "46px",
            formatter: (requirement: CcRequirement) => <TableMenu onClick={(e) => showContextMenu(e, requirement)} />,
        },
        {
            field: "uniqueId",
            header: lang.uniqueId,
            display: displayColumns.uniqueId,
            sortable: true,
            collapsible: true,
            cellMinWidth: "150px",
            cellMaxWidth: "250px",
            onClick: (requirement: CcRequirement) => onClick(requirement),
        },
        {
            field: "name",
            header: lang.name,
            display: displayColumns.name,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            onClick: (requirement: CcRequirement) => onClick(requirement),
        },
        {
            field: "revision",
            header: lang.revisionList,
            display: displayColumns.revision,
            cellMinWidth: "200px",
            overflowVisible: true,
            formatter: (requirement: CcRequirement) => <RequirementRevisionList requirement={requirement} />,
        },
        {
            field: "type",
            header: lang.type,
            display: displayColumns.type,
            sortable: true,
            cellMinWidth: "150px",
            onClick: (requirement: CcRequirement) => onClick(requirement),
            formatter: (requirement: CcRequirement) => {
                const type = typeList[requirement.typeId]
                return type ? type.name : <NotAssigned />
            },
        },
        {
            field: "priority",
            header: lang.priority,
            display: displayColumns.priority,
            sortable: true,
            cellMinWidth: "150px",
            onClick: (requirement: CcRequirement) => onClick(requirement),
            formatter: (requirement: CcRequirement) => {
                const priority = priorityList[requirement.priorityId]
                return priority ? priority.name : <NotAssigned />
            },
        },
        {
            field: "status",
            header: lang.status,
            display: displayColumns.status,
            sortable: true,
            cellMinWidth: "150px",
            onClick: (requirement: CcRequirement) => onClick(requirement),
            formatter: (requirement: CcRequirement) => {
                const status = statusList[requirement.statusId]
                return status ? status.name : <NotAssigned />
            },
        },
    ]

    // add columns for each level
    levelIds.forEach((levelId) => {
        const columnId = `level_${levelId}`
        columns.push({
            field: columnId,
            header: levelList[levelId].name,
            display: displayColumns[columnId],
            cellMinWidth: "150px",
            onClick: (requirement: CcRequirement) => onClick(requirement),
            formatter: (requirement: CcRequirement) => {
                const requirementState = requirementStateList[requirement.id] || null
                const settings = requirementState && requirementState.settings
                    ? JSON.parse(requirementState.settings)
                    : {}
                const userId = settings[levelId] || null
                const user = userList[userId]
                return user ? <UserIcon user={user} size="sm" /> : <NotAssigned />
            },
        })

        const columnAnswerId = `${columnId}_answer`
        columns.push({
            field: columnAnswerId,
            header: `${levelList[levelId].name} ${lang.answer}`,
            display: displayColumns[columnAnswerId],
            cellMinWidth: "150px",
            onClick: (requirement: CcRequirement) => onClick(requirement),
            formatter: (requirement: CcRequirement) => {
                const requirementHistory = requirementHistoryList[requirement.id] || []
                const requirementState = requirementStateList[requirement.id] || null

                let levelHistory: CcRequirementHistory = null
                for (let i = requirementHistory.length - 1; i >= 0; i--) {
                    const history = requirementHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage && history.prevLevelId === levelId) {
                        levelHistory = history
                        break
                    }
                    if (!history.systemMessage && history.parentId === null) { // take answer only from the last branch
                        break
                    }
                }

                if (
                    requirementState
                    && requirementState.levelId === levelId
                    && (requirementState.verifierId === userData.userId || viewDraftsAccess)
                    && tempAnswerList[requirement.id]
                    && tempAnswerList[requirement.id][requirementState.verifierId]
                ) {
                    return <DraftSaved />
                }
                if (levelHistory) {
                    const response = responseList[levelHistory.responseId] || null
                    return response ? response.response : <Empty />
                }
                return <Empty />
            },
        })
    })

    columns.push({
        field: "state",
        header: lang.state,
        display: displayColumns.state,
        cellMinWidth: "200px",
        style: {
            fontSize: "13px",
        },
        onClick: (requirement: CcRequirement) => onClick(requirement),
        formatter: (requirement: CcRequirement) => {
            const requirementHistory = requirementHistoryList[requirement.id] || []
            let lastHistory: CcRequirementHistory = null

            // get the last history row made by user, not by 'system'
            for (let i = requirementHistory.length - 1; i >= 0; i--) {
                const history = requirementHistory[i]
                if (history.workflow === WORKFLOW_RERUN) {
                    break
                }
                if (!history.systemMessage) {
                    lastHistory = history
                    break
                }
            }

            const requirementState = requirementStateList[requirement.id] || null
            const level = requirementState ? levelList[requirementState.levelId] : null
            const verifier = requirementState ? userList[requirementState.verifierId] : null

            if (lastHistory && lastHistory.nextStatus === APPROVED) {
                const response = responseList[lastHistory.responseId]
                return `${lang.status}: ${response && response.approved === YES ? lang.approved : lang.notApproved}`
            }

            if (level) {
                const firstLevel = level.id === levelIds[0]
                return (
                    <div>
                        <div>
                            {`${level.name}: `}
                            {verifier ? <UserIcon user={verifier} size="sm" /> : <NotAssigned />}
                        </div>
                        <div>
                            {`${lang.status}: ${firstLevel ? lang.open : lang.verification}`}
                        </div>
                    </div>
                )
            }

            if (!level && lastHistory) { // approval level was removed or not set
                return (
                    <div>
                        {`${lang.status}: ${lang.verification}`}
                    </div>
                )
            }

            if (!level && !lastHistory) { // no any data
                return (
                    <div>
                        {`${lang.status}: ${lang.open}`}
                    </div>
                )
            }

            return null
        },
    })

    if (editAccess) {
        columns.unshift({
            field: "select",
            headerFormatter: () => <InputSelectAll />,
            cellWidth: "46px",
            cellMinWidth: "46px",
            formatter: (requirement: CcRequirement) => (
                <CheckButton
                    checked={selectedRequirementIds.includes(requirement.id)}
                    padding={false}
                    margin={false}
                    onChange={() => selectRequirement(requirement.id)}
                />
            ),
        })
    }

    return { columns, displayColumns }
}
export default Columns
